/**
 * FontPreparation - Font validation and document processing pipeline
 *
 * Extracted from EdgeDocsCanvas.V3.tsx (lines 694-721) as part of Phase 3 Step 3.
 *
 * This module handles the initial font preparation steps of the rendering pipeline:
 * - Font validation and resolution
 * - Document processing with page setup conversion
 * - Initialization input preparation for WASM
 *
 * This is the first stage of the rendering pipeline, establishing the font context
 * before text layout and glyph rendering can occur.
 */

import { validateAndResolveFont } from '../../../../../rendering/canvas';
import { DocumentProcessor } from '../../../../../rendering/pipeline';
import type { FontResolver } from '../../../../../services/FontResolver';

/**
 * Font preparation input parameters
 */
export interface FontPreparationParams {
  /** Text to render */
  renderText: string;
  /** Document state containing full document data */
  documentState: any;
  /** Font family name */
  fontFamily: string;
  /** Font weight (e.g., 400, 700) */
  fontWeight: number;
  /** Font style (e.g., 'normal', 'italic') */
  fontStyle: string;
  /** Font size in pixels */
  fontSizePx: number;
  /** Font resolver instance */
  fontResolver: FontResolver;
  /** Debug logging function */
  debugLog: (...args: any[]) => void;
}

/**
 * Font preparation result
 */
export interface FontPreparationResult {
  /** Resolved font unique identifier */
  resolvedFontUid: string;
  /** WASM page setup configuration */
  wasmPageSetup: any;
  /** Converted page setup for rendering */
  convertedPageSetup: any;
  /** Initialization input for WASM */
  initInput: any;
}

/**
 * Prepares font and document for rendering
 *
 * This function performs two critical operations:
 * 1. Font validation - ensures the requested font is available and resolves its UID
 * 2. Document processing - converts page setup and prepares WASM initialization input
 *
 * @param params - Font preparation parameters
 * @returns Font preparation result, or null if font validation failed
 *
 * @example
 * ```typescript
 * const fontPrep = prepareFontAndDocument({
 *   renderText: "Hello, world!",
 *   documentState,
 *   fontFamily: "Inter",
 *   fontWeight: 400,
 *   fontStyle: "normal",
 *   fontSizePx: 16,
 *   fontResolver,
 *   debugLog
 * });
 *
 * if (!fontPrep) {
 *   console.error("Font validation failed");
 *   return;
 * }
 *
 * const { resolvedFontUid, wasmPageSetup, initInput } = fontPrep;
 * // Proceed with rendering...
 * ```
 */
export function prepareFontAndDocument(
  params: FontPreparationParams
): FontPreparationResult | null {
  const {
    renderText,
    documentState,
    fontFamily,
    fontWeight,
    fontStyle,
    fontSizePx,
    fontResolver,
    debugLog
  } = params;

  // Validate and resolve font for rendering
  const fontValidation = validateAndResolveFont({
    fontResolver,
    fontFamily,
    fontWeight,
    fontStyle,
    debugLog
  });

  if (!fontValidation) {
    return null; // Font validation failed
  }

  const { resolvedFontUid } = fontValidation;

  // Process document with page setup conversion and initialization input preparation
  const docResult = DocumentProcessor.processDocument({
    text: renderText,
    documentState,
    fontFamily,
    fontWeight,
    fontStyle,
    fontSizePx,
    resolvedFontUid,
    debugLog
  });

  const { wasmPageSetup, convertedPageSetup, initInput } = docResult;

  return {
    resolvedFontUid,
    wasmPageSetup,
    convertedPageSetup,
    initInput
  };
}
